import React, { useState, useMemo, useEffect } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    flexRender,
    ColumnDef,
    PaginationState
} from '@tanstack/react-table';
import { GetUsers, dataUser } from '../../services/dashboard/GetUsers';
import { changeUserStatus } from "../../services/dashboard/ChangeStatusUser";
import '../../styles/dash/BotsSection.css';
import EditUserModal from './EditUserModal';
import LoadingSpinner from '../utility/LoadingSpinner';
import { dataRole } from "../../services/dashboard/GetUsers";
import CreateUserModal from './CreateUserModal';
import { useNavigate } from 'react-router-dom';

// Interfaces para los permisos
interface Permission {
    Id: number;
    IdAction: number;
    Action: string;
    IdModule: number;
    ModuleName: string;
}

interface Role {
    Id: number;
    RoleName: string;
    Permissions: Permission[];
}

const Users: React.FC = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<dataUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState<dataUser | null>(null);
    const [roles, setRoles] = useState<dataRole[]>([]);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false); // Estado general para la carga
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [permisos, setPermisos] = useState<number[]>([]);

    const fetchUsers = async () => {
        try {
            const data = await GetUsers();
            setUsers(data.users);
            setRoles(data.roles);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error desconocido');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let hasPermission = false;
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const userObject = JSON.parse(userData);
                const userDataParsed = JSON.parse(userObject.userData || "{}");

                if (userDataParsed?.roles?.length) {
                    const permisosAplanados = userDataParsed.roles.flatMap((rol: Role) =>
                        rol.Permissions.map((p: Permission) => p.IdAction)
                    );
                    setPermisos(permisosAplanados);
                    if (permisosAplanados.includes(12)) {
                        hasPermission = true;
                    }
                }
            } catch (err) {
                console.error('Error parsing user data for permissions:', err);
            }
        }
        if (!hasPermission) {
            navigate('/login');
        } else {
            fetchUsers();
        }
    }, [navigate]);

    const handleToggleStatus = async (id: number, currentStatus: number) => {
        // Guardar el estado actual por si necesitamos revertir
        const originalStatus = currentStatus;

        // Actualización optimista: cambiar UI inmediatamente
        setUsers(prevUsers =>
            prevUsers.map(user =>
                user.id === id
                    ? { ...user, status: currentStatus === 1 ? 0 : 1 }
                    : user
            )
        );

        setIsUpdatingStatus(true);

        try {
            // Llamar al servicio para cambiar el estado
            await changeUserStatus({
                idUser: id,
                status: currentStatus === 1 ? 0 : 1
            });

            // Si todo sale bien, no necesitamos hacer nada más
        } catch (error) {
            // Revertir el cambio en caso de error
            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user.id === id
                        ? { ...user, status: originalStatus }
                        : user
                )
            );

            // Mostrar mensaje de error
            setError('No se pudo cambiar el estado del usuario. Intente de nuevo.');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const columns = useMemo<ColumnDef<dataUser>[]>(() => [
        {
            id: 'actions',
            header: 'Acciones',
            cell: ({ row }) => {
                const user = row.original;
                const powerButtonColorClass = user.status === 1 ? 'power-off' : 'power-on';
                const powerButtonText = user.status === 1 ? 'Desactivar' : 'Activar';

                return (
                    <div className="table-actions">
                        <button
                            className={`action-button power ${powerButtonColorClass}`}
                            title={powerButtonText}
                            onClick={() => handleToggleStatus(user.id, user.status)}
                            disabled={isUpdatingStatus || !permisos.includes(16)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="9" />
                                <line x1="12" y1="4" x2="12" y2="8" />
                            </svg>
                        </button>

                        <button
                            className="action-button edit"
                            title="Editar"
                            onClick={() => {
                                setEditingUser(user);
                                setShowEditModal(true);
                            }}
                            disabled={isUpdatingStatus || !permisos.includes(17)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="white"
                                viewBox="0 0 24 24"
                            >
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
                                <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.41l-2.34-2.34a1.003 1.003 0 0 0-1.41 0L15.13 4.9l3.75 3.75 1.83-1.61z" />
                            </svg>
                        </button>
                    </div>
                );
            },
        },
        {
            accessorKey: 'user',
            header: 'Usuario',
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'email',
            header: 'Email',
            cell: info => info.getValue(),
        },
        {
            id: 'roles',
            header: 'Roles',
            cell: ({ row }) => {
                const roles = row.original.roles;
                return roles.length > 0
                    ? roles.map(r => r.nameRole).join(', ')
                    : <em>Sin roles</em>;
            }
        },
        {
            accessorKey: 'status',
            header: 'Estado',
            cell: info => {
                const status = info.getValue<number>();
                const statusText = status === 1 ? 'Activo' : 'Inactivo';
                return (
                    <span className={`status-text ${statusText.toLowerCase()}`}>
                        {statusText}
                    </span>
                );
            },
        },
    ], [isUpdatingStatus, permisos]);

    const table = useReactTable({
        data: users,
        columns,
        state: {
            globalFilter,
            pagination,
        },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    function getPaginationRange(current: number, total: number): (number | string)[] {
        if (total <= 1) return [1];

        const delta = 1;
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let l = 0;

        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
                range.push(i);
            }
        }

        for (let i of range) {
            if (l > 0) {
                if (Number(i) - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (Number(i) - l !== 1) {
                    rangeWithDots.push("...");
                }
            }
            rangeWithDots.push(i);
            l = Number(i);
        }

        return rangeWithDots;
    }

    return (
        <div className="dashboard-container">

            <div className="demo-request-table-container t">
                {/* Barra de búsqueda */}
                <div className="table-header-toolbar">
                    <button
                        className="add-bot-tab-button"
                        onClick={() => setShowCreateModal(true)}
                        disabled={!permisos.includes(14)}
                    >
                        Crear Nuevo Usuario
                    </button>

                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Buscar usuarios..."
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="global-filter"
                        />
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <div className="error-message">Error: {error}</div>
                ) : users.length === 0 ? (
                    <div className="no-data-message">No se encontraron usuarios</div>
                ) : (
                    <>
                        {/* Spinner de carga general */}
                        {isUpdatingStatus && (
                            <div className="overlay-spinner">
                                <LoadingSpinner />
                                <div className="spinner-text">Actualizando estado...</div>
                            </div>
                        )}

                        {/* Tabla de usuarios */}
                        <div className="chatbots-table-container">
                            <table className="chatbots-table">
                                <colgroup>
                                    <col style={{ width: '15%' }} /><col style={{ width: '15%' }} /><col style={{ width: '30%' }} /><col style={{ width: '25%' }} /><col style={{ width: '15%' }} />
                                </colgroup>
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => (
                                                <th key={header.id}>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map(row => (
                                        <tr key={row.id}>
                                            {row.getVisibleCells().map(cell => (
                                                <td key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginación */}
                        <div className="pagination-controls">
                            <button
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage() || isUpdatingStatus}
                            >
                                ←
                            </button>

                            {getPaginationRange(
                                table.getState().pagination.pageIndex + 1,
                                table.getPageCount()
                            ).map((page, idx) =>
                                page === "..." ? (
                                    <span key={idx} className="dots">...</span>
                                ) : (
                                    <button
                                        key={idx}
                                        className={
                                            table.getState().pagination.pageIndex + 1 === page
                                                ? "active-page"
                                                : ""
                                        }
                                        onClick={() => table.setPageIndex(Number(page) - 1)}
                                        disabled={isUpdatingStatus}
                                    >
                                        {page}
                                    </button>
                                )
                            )}

                            <button
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage() || isUpdatingStatus}
                            >
                                →
                            </button>
                        </div>
                    </>
                )}
            </div>

            {showEditModal && editingUser && (
                <EditUserModal
                    user={editingUser}
                    roles={roles}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingUser(null);
                    }}
                    onSuccess={(updatedUser) => {
                        setUsers(prev =>
                            prev.map(u => u.id === updatedUser.id ? updatedUser : u)
                        );
                    }}
                />
            )}

            {showCreateModal && (
                <CreateUserModal
                    roles={roles}
                    onClose={() => {
                        setShowCreateModal(false);
                    }}
                    onSuccess={(newUser) => {
                        setUsers(prevUsers => [...prevUsers, newUser]);
                        setShowCreateModal(false);
                    }}
                    fetch={fetchUsers}
                />
            )}


        </div>
    );
};

export default Users;